import { createContext, ReactElement, useReducer } from "react"
import axios from "utils/axios"
import { FETCH_ALLOWED_OFFER_LINKS, FETCH_ALBUMS_DEFAULT_FILTERS, FETCH_MESSAGE_CONTENTS, SAVE_MESSAGE_CONTENT, DELETE_MESSAGE_CONTENT } from "../store/reducers/actions"
import { mapPaginatedDataToCamelCase, mapToCamelCase } from "../utils/caseTransformation"
import { PaginatedData } from "types/root"
import { getFilterText, toastMessage } from "../utils"
import { AllowedOffersLink, DefaultAlbumFilters, MessageContentType, SystemContextType } from "../types/system"
import systemsReducer, { initialSystemState } from "../store/reducers/system"

const SystemContext = createContext<SystemContextType | null>(null)

export const SystemsProvider = ({ children }: { children: ReactElement }) => {
  const [state, dispatch] = useReducer(systemsReducer, initialSystemState)

  const fetchAllowedOffersLinks = async (limit: number, offset: number, filter?: any, orderBy?: string) => {
    const response = await axios(
      `/system/allowed-offer-links/?limit=${limit}&offset=${offset}${getFilterText(filter)}${
        orderBy ? `&order_by=${orderBy}` : ""
      }`,
      { method: "get" }
    )

    dispatch({
      type: FETCH_ALLOWED_OFFER_LINKS,
      payload: {
        ...state,
        paginatedAllowedOffersLinks: mapPaginatedDataToCamelCase<PaginatedData<AllowedOffersLink>>(response.data)
      }
    })
  }

  const saveAllowedOffersLink = async (allowedOfferLink: AllowedOffersLink) => {
    const response = await axios(`/system/allowed-offer-links/save/`, { method: "post", data: { ...allowedOfferLink } })
    if (!response.data.entity) {
      return false
    }

    const results = [...state.paginatedAllowedOffersLinks.results]
    if (!allowedOfferLink.id) {
      results.unshift(mapToCamelCase(response.data.entity))
    } else {
      const allowedOfferLink = mapToCamelCase(response.data.entity)
      results.forEach((result) => {
        if (result.id === allowedOfferLink.id) {
          result.expireDate = allowedOfferLink.expireDate
        }
        return result
      })
    }

    dispatch({
      type: FETCH_ALLOWED_OFFER_LINKS,
      payload: {
        ...state,
        paginatedAllowedOffersLinks: { ...state.paginatedAllowedOffersLinks, results: [...results] }
      }
    })

    return true
  }

  const deleteAllowedOffersLink = async (id: number) => {
    const response = await axios(`/system/allowed-offer-links/remove/?id=${id}`, { method: "delete" })
    if (!response.data.success) {
      return false
    }

    const results = state.paginatedAllowedOffersLinks.results.filter((result) => result.id !== id)

    dispatch({
      type: FETCH_ALLOWED_OFFER_LINKS,
      payload: {
        ...state,
        paginatedAllowedOffersLinks: { ...state.paginatedAllowedOffersLinks, results: [...results] }
      }
    })

    return true
  }

  const fetchAlbumDefaultFilters = async () => {
    const response = await axios(`/system/setting/album_default_filters/`, { method: "get" })

    dispatch({
      type: FETCH_ALBUMS_DEFAULT_FILTERS,
      payload: { ...state, defaultAlbumFilters: mapToCamelCase(response.data.result) as DefaultAlbumFilters }
    })
  }

  const fetchMessageContents = async () => {
    const response = await axios(
      `/system/message-content/`,
      { method: "get" }
    )
    const messageContentList = response.data.results.map((messageContent: MessageContentType) => {
      return messageContent
    })
    dispatch({
      type: FETCH_MESSAGE_CONTENTS,
      payload: {
        ...state,
        messageContents: messageContentList
      }
    })
  }

  const saveMessageContent = async (messageContent: MessageContentType) => {
      const response = await axios(`/system/message-content/save/`, { method: "post", data: { ...messageContent } })

      const existingMessageContents = [...state.messageContents]
      let updatedList = []
      if (!response.data) {
        toastMessage(`Some error happened while ${messageContent?.id ? "updating" : "creating"} the content.`, "error")
        return false
      } else {
        if (messageContent?.id) {
          updatedList = [...existingMessageContents].map((mc: MessageContentType) =>
            messageContent.id === mc?.id ? response.data : mc
          )
          toastMessage(`Content has been updated successfully.`, "success")
        } else {
          updatedList = [...existingMessageContents, response.data]
          toastMessage(`Content has been created successfully.`, "success")
        }
      }
      dispatch({
        type: SAVE_MESSAGE_CONTENT,
        payload: {
          ...state,
          messageContents: updatedList
        }
      })
      return true
  }

  const deleteMessageContent = async (id: number) => {
    const response = await axios(`/system/message-content/delete_message_content/?id=${id}`, { method: "delete" })
    if (!response.data.success) {
      toastMessage("Error deleting message content", "error")
      return false
    }

    const filteredMessageContents = state.messageContents.filter((messageContent) => messageContent.id !== id)

    dispatch({
      type: DELETE_MESSAGE_CONTENT,
      payload: {
        ...state,
        messageContents: filteredMessageContents
      }
    })
    toastMessage("Successfully deleted message content", "success")
    return true
  }
    
  return (
    <SystemContext.Provider
      value={{
        ...state,
        fetchAllowedOffersLinks,
        saveAllowedOffersLink,
        deleteAllowedOffersLink,
        fetchAlbumDefaultFilters,
        fetchMessageContents,
        saveMessageContent,
        deleteMessageContent
      }}
    >
      {children}
    </SystemContext.Provider>
  )
}

export default SystemContext
